import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  TrendingUp,
  Users,
  RefreshCw,
  Download,
  Clock,
  Building2,
  Map as MapIcon,
  Table2,
  ArrowRight,
  Cpu,
  Sparkles,
  Megaphone,
  Activity,
  FileBarChart
} from 'lucide-react';
import { getDashboard, fetchNewEvents, triggerAIProcessing, getStats, previewCleanup, applyCleanup,
  previewClinicNames, applyClinicNames,
  startBackgroundAnalysis, stopBackgroundAnalysis, getBackgroundStatus } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import StatTile from '../components/ui/StatTile';
import { compactNumber } from '../utils/format';

const COLORS = ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4'];

function StatCard({ icon: Icon, label, value, subValue, color = 'dental' }) {
  const colorClasses = {
    dental: 'bg-dental-50 text-dental-600',
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which long-running action is in progress: null | 'full' | 'fetch' | 'analyse' | 'cleanup'
  const [busy, setBusy] = useState(null);
  const [fetchMessage, setFetchMessage] = useState(null);
  // Preview result for the Fix Duplicates flow (null = no pending preview).
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [clinicNamePreview, setClinicNamePreview] = useState(null);
  const [unprocessed, setUnprocessed] = useState(null);
  // Background analysis runs server-side and survives tab close; we just poll it.
  const [bgRunning, setBgRunning] = useState(false);

  // Poll background-analysis status while it runs (and once on mount, so a job
  // started earlier still shows progress after a refresh / reopened tab). A
  // `stalled` job (flagged running but its worker died) is NOT re-kicked
  // automatically — AI analysis only runs when the user clicks Analyse.
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const res = await getBackgroundStatus();
        if (!active) return;
        const { running, remaining, stalled } = res.data.data;
        setBgRunning(running);
        setUnprocessed(remaining);
        if (running) {
          setFetchMessage({
            type: 'info',
            text: stalled
              ? `Analysis paused — ${remaining} ads remaining. Click Analyse to continue.`
              : `Analysing in background... ${remaining} ads remaining`,
          });
        }
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { active = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    document.title = 'Dashboard | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await getDashboard();
      setData(response.data.data);
      // Surface the count of unanalysed ads (drives the Analyse Ads button).
      try {
        const statsRes = await getStats();
        setUnprocessed(statsRes.data.data.unprocessedAds ?? null);
      } catch (_) {}
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
    }
  };

  // Phase 1 only: fetch raw ads (no AI). Returns the number of new ads found.
  const runFetchAds = async () => {
    let startIndex = 0;
    let totalNewAds = 0;
    do {
      // 2 queries/call so each can paginate deeper within the 50s budget.
      const response = await fetchNewEvents({ startIndex, batchSize: 2 });
      const d = response.data.data;
      totalNewAds += d.totalNewAds || 0;
      startIndex = d.nextIndex;
      setFetchMessage({
        type: 'info',
        text: startIndex
          ? `Fetching ads... ${d.progress.done}/${d.progress.total} queries, ${totalNewAds} new ads`
          : `Fetched ${totalNewAds} new ads.`,
      });
    } while (startIndex !== null);
    return totalNewAds;
  };

  // Phase 2 only: analyse unprocessed ads in batches until none remain.
  const runAnalyseAds = async () => {
    let remaining = Infinity;
    let safety = 0;
    let consecutiveNoop = 0;
    while (remaining > 0 && safety < 500) {
      safety++;
      // 16 ads/call — analysed in parallel groups server-side, bounded by the
      // 38s deadline guard, so the 617-ad queue drains far faster than 5/call.
      const aiRes = await triggerAIProcessing(16);
      const d = aiRes.data.data;
      remaining = d.remaining;
      setUnprocessed(remaining);
      setFetchMessage({
        type: 'info',
        text: remaining > 0 ? `Analysing... ${remaining} ads remaining` : 'AI analysis complete!',
      });
      if (d.processed === 0 && (d.failed || 0) === 0) break;
      if (d.processed === 0 && (d.failed || 0) > 0) {
        consecutiveNoop++;
        if (consecutiveNoop >= 3) break;
      } else {
        consecutiveNoop = 0;
      }
    }
  };

  // Manual: fetch raw ads only.
  const handleFetchAds = async () => {
    try {
      setBusy('fetch');
      setFetchMessage(null);
      const totalNewAds = await runFetchAds();
      setFetchMessage({
        type: 'success',
        text: totalNewAds > 0
          ? `Fetched ${totalNewAds} new ads. Click "Analyse Ads" to process them.`
          : 'No new ads found (all up to date).',
      });
      setTimeout(() => loadDashboard(), 1500);
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Failed to fetch ads') });
    } finally {
      setBusy(null);
    }
  };

  // Analyse ads in the BACKGROUND. Each call drains a chunk of the queue
  // in-process (server-side) and returns `running: true` if it stopped early
  // with work left; we loop to keep it going while the tab is open. If the tab
  // closes mid-run the job pauses — nothing resumes it until Analyse is clicked
  // again. A stop request breaks the loop.
  const handleAnalyseAds = async () => {
    try {
      setFetchMessage(null);
      let running = true;
      while (running) {
        const res = await startBackgroundAnalysis(16);
        const d = res.data.data;
        running = d.running;
        setBgRunning(running);
        setUnprocessed(d.remaining);
        setFetchMessage({
          type: running ? 'info' : 'success',
          text: running
            ? `Analysing... ${d.remaining} ads remaining. Keep this tab open — closing it pauses analysis.`
            : 'All ads analysed.',
        });
      }
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Failed to start analysis') });
    }
  };

  // Stop the background analysis loop.
  const handleStopAnalyse = async () => {
    try {
      await stopBackgroundAnalysis();
      setBgRunning(false);
      setFetchMessage({ type: 'info', text: 'Stopping background analysis after the current batch...' });
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Failed to stop') });
    }
  };

  // Step 1: preview the AI duplicate plan (writes nothing).
  const handleCleanupPreview = async () => {
    try {
      setBusy('cleanup');
      setFetchMessage(null);
      setCleanupPreview(null);
      const res = await previewCleanup();
      setCleanupPreview(res.data.data);
      setFetchMessage({ type: 'info', text: res.data.message });
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Preview failed') });
    } finally {
      setBusy(null);
    }
  };

  // Step 2: apply exactly the previewed merges (soft-merge, reversible).
  const handleCleanupApply = async () => {
    try {
      setBusy('cleanup');
      const plan = cleanupPreview?.duplicatePlan || [];
      const res = await applyCleanup(plan);
      setFetchMessage({ type: 'success', text: res.data.message });
      setCleanupPreview(null);
      setTimeout(() => loadDashboard(), 1500);
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Apply failed') });
    } finally {
      setBusy(null);
    }
  };

  const handleCleanupCancel = () => {
    setCleanupPreview(null);
    setFetchMessage(null);
  };

  // Clinic-name canonicalisation, same preview-then-apply shape as duplicates:
  // "VK SMILE Studio", "VK Smile Studio" and "VK Smiles" become one clinic.
  const handleClinicNamesPreview = async () => {
    try {
      setBusy('clinics');
      setFetchMessage(null);
      setClinicNamePreview(null);
      const res = await previewClinicNames();
      setClinicNamePreview(res.data.data);
      setFetchMessage({ type: 'info', text: res.data.message });
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Preview failed') });
    } finally {
      setBusy(null);
    }
  };

  const handleClinicNamesApply = async () => {
    try {
      setBusy('clinics');
      const res = await applyClinicNames();
      setFetchMessage({ type: 'success', text: res.data.message });
      setClinicNamePreview(null);
      setTimeout(() => loadDashboard(), 1500);
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Apply failed') });
    } finally {
      setBusy(null);
    }
  };

  // Full pipeline: fetch raw ads, then analyse them (the original one-click flow).
  const handleFetchNewEvents = async () => {
    try {
      setBusy('full');
      setFetchMessage(null);

      let eventsBefore = 0;
      try { eventsBefore = (await getStats()).data.data.totalEvents || 0; } catch (_) {}

      const totalNewAds = await runFetchAds();
      await runAnalyseAds();

      let newEventsCreated = 0;
      try { newEventsCreated = Math.max(0, ((await getStats()).data.data.totalEvents || 0) - eventsBefore); } catch (_) {}

      setFetchMessage({
        type: 'success',
        text: newEventsCreated > 0
          ? `Done! ${newEventsCreated} new events added (${totalNewAds} ads scanned).`
          : totalNewAds > 0
            ? `Done! ${totalNewAds} ads scanned — no new events found (all existing or non-events).`
            : 'Done! No new ads found (all up to date).',
      });

      setTimeout(() => loadDashboard(), 2000);
    } catch (err) {
      setFetchMessage({ type: 'error', text: getApiErrorMessage(err, 'Failed to fetch events') });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dental-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 border border-red-200">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => { setError(null); loadDashboard(); }}
          className="btn-primary mt-4"
        >
          Try again
        </button>
      </div>
    );
  }

  const { overview, impressions, eventsTimeline, eventsByLocation, upcomingEvents, treatmentTypes, topClinics } = data || {};

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="page-title mb-1">Dashboard</h1>
          <p className="text-sm text-ink-500">Track competitor dental events and analyze their performance</p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2">
          <button
            onClick={loadDashboard}
            disabled={loading || !!busy}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleFetchAds}
            disabled={!!busy}
            className="btn-secondary flex items-center justify-center gap-2"
            title="Download new raw ads only (no AI analysis)"
          >
            <Download className={`w-4 h-4 ${busy === 'fetch' ? 'animate-bounce' : ''}`} />
            {busy === 'fetch' ? 'Fetching ads...' : 'Fetch Ads'}
          </button>
          {bgRunning ? (
            <button
              onClick={handleStopAnalyse}
              className="btn-secondary flex items-center justify-center gap-2 text-red-600 ring-red-200"
              title="Stop the background analysis after the current batch"
            >
              <Cpu className="w-4 h-4 animate-pulse" />
              Stop ({compactNumber(unprocessed || 0)})
            </button>
          ) : (
            <button
              onClick={handleAnalyseAds}
              disabled={!!busy}
              className="btn-secondary flex items-center justify-center gap-2"
              title="Analyse unprocessed ads in the background (keeps running if you close the tab)"
            >
              <Cpu className="w-4 h-4" />
              Analyse Ads
              {unprocessed > 0 && (
                <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                  {compactNumber(unprocessed)}
                </span>
              )}
            </button>
          )}
          <button
            onClick={handleCleanupPreview}
            disabled={!!busy}
            className="btn-secondary flex items-center justify-center gap-2"
            title="AI scans for duplicate events and previews them before anything changes"
          >
            <Sparkles className={`w-4 h-4 ${busy === 'cleanup' ? 'animate-spin' : ''}`} />
            {busy === 'cleanup' ? 'Scanning...' : 'Fix Duplicates'}
          </button>
          <button
            onClick={handleClinicNamesPreview}
            disabled={!!busy}
            className="btn-secondary flex items-center justify-center gap-2"
            title="Collapses spelling variants of the same clinic onto one name — previewed before anything changes"
          >
            <Building2 className={`w-4 h-4 ${busy === 'clinics' ? 'animate-spin' : ''}`} />
            {busy === 'clinics' ? 'Scanning...' : 'Fix Clinic Names'}
          </button>
          <button
            onClick={handleFetchNewEvents}
            disabled={!!busy}
            className="btn-primary flex items-center justify-center gap-2"
            title="Fetch new ads and analyse them in one go"
          >
            <Download className={`w-4 h-4 ${busy === 'full' ? 'animate-bounce' : ''}`} />
            {busy === 'full' ? 'Working...' : 'Fetch New Events'}
          </button>
        </div>
      </div>

      {/* Fetch Status Message */}
      {fetchMessage && (
        <div className={`p-4 rounded-lg ${
          fetchMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : fetchMessage.type === 'error'
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-blue-50 border border-blue-200 text-blue-700'
        }`}>
          <p className="text-sm">{fetchMessage.text}</p>
        </div>
      )}

      {/* Duplicate preview — review the AI plan before applying (soft-merge, reversible) */}
      {cleanupPreview && (
        <div className="card border border-dental-200 bg-dental-50/40">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-semibold text-gray-900">
                {cleanupPreview.duplicatePlan?.length
                  ? `${cleanupPreview.duplicatePlan.length} duplicate event${cleanupPreview.duplicatePlan.length === 1 ? '' : 's'} found`
                  : 'No duplicate events found'}
              </h3>
              <p className="text-sm text-gray-500">
                {cleanupPreview.aiUsed ? 'Detected by AI' : 'Detected by rules'} · also fixes {cleanupPreview.clinicsMerged} clinic & {cleanupPreview.locationsMerged} location names
              </p>
            </div>
          </div>

          {cleanupPreview.duplicatePlan?.length > 0 && (
            <ul className="max-h-64 overflow-auto divide-y divide-gray-100 mb-4 text-sm">
              {cleanupPreview.duplicatePlan.map((m, i) => (
                <li key={i} className="py-2 flex items-center gap-2">
                  <span className="text-gray-500 line-through truncate max-w-[45%]">{m.dupLabel}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-gray-900 font-medium truncate max-w-[45%]">{m.keeperLabel}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleCleanupApply}
              disabled={!!busy || !(cleanupPreview.duplicatePlan?.length || cleanupPreview.clinicsMerged || cleanupPreview.locationsMerged || cleanupPreview.adsRequeued)}
              className="btn-primary flex items-center justify-center gap-2"
            >
              <Sparkles className={`w-4 h-4 ${busy === 'cleanup' ? 'animate-spin' : ''}`} />
              {busy === 'cleanup' ? 'Applying...' : 'Apply'}
            </button>
            <button onClick={handleCleanupCancel} disabled={!!busy} className="btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Clinic-name preview — nothing is renamed until this is applied */}
      {clinicNamePreview && (
        <div className="card border border-dental-200 bg-dental-50/40">
          <div className="mb-3">
            <h3 className="font-semibold text-gray-900">
              {clinicNamePreview.groups?.length
                ? `${clinicNamePreview.groups.length} clinic name${clinicNamePreview.groups.length === 1 ? '' : 's'} to tidy`
                : 'Clinic names are already consistent'}
            </h3>
            <p className="text-sm text-gray-500">
              {clinicNamePreview.updatedEvents} of {clinicNamePreview.totalEvents} events would be
              renamed · {clinicNamePreview.distinctBefore} distinct names today
            </p>
          </div>

          {clinicNamePreview.groups?.length > 0 && (
            <ul className="max-h-64 overflow-auto divide-y divide-gray-100 mb-4 text-sm">
              {clinicNamePreview.groups.map((g) => (
                <li key={g.to} className="py-2 flex items-center gap-2">
                  <span className="text-gray-500 truncate max-w-[45%]" title={g.from.join(', ')}>
                    {g.from.join(', ')}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-gray-900 font-medium truncate max-w-[45%]">{g.to}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleClinicNamesApply}
              disabled={!!busy || !clinicNamePreview.updatedEvents}
              className="btn-primary flex items-center justify-center gap-2"
            >
              <Building2 className={`w-4 h-4 ${busy === 'clinics' ? 'animate-spin' : ''}`} />
              {busy === 'clinics' ? 'Applying...' : 'Apply'}
            </button>
            <button onClick={() => setClinicNamePreview(null)} disabled={!!busy} className="btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatTile icon={Calendar} label="Total events" value={overview?.totalEvents || 0} tone="dental" />
        <StatTile icon={Megaphone} label="Ads collected" value={overview?.totalAds || 0} tone="blue" />
        <StatTile icon={Activity} label="Ads live now" value={overview?.activeAds || 0} tone="green" />
        <StatTile icon={TrendingUp} label="AI processed" value={overview?.processedAds || 0} subValue={overview?.unprocessedAds ? `${overview.unprocessedAds} pending` : 'all done'} tone="accent" />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { to: '/reports', icon: FileBarChart, label: 'Reports', desc: 'Lead time, spend, lead forms', tone: 'bg-indigo-50 text-indigo-600 ring-indigo-100' },
          { to: '/clinics', icon: Building2, label: 'Competitor clinics', desc: 'Where they go & how often', tone: 'bg-dental-50 text-dental-600 ring-dental-100' },
          { to: '/cities', icon: Table2, label: 'Cities & opportunities', desc: 'Demand vs competition', tone: 'bg-accent-50 text-accent-600 ring-accent-100' },
          { to: '/map', icon: MapIcon, label: 'Map', desc: 'Geographic footprint', tone: 'bg-amber-50 text-amber-600 ring-amber-100' },
        ].map((q) => (
          <Link key={q.to} to={q.to} className="card card-hover flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ring-1 ${q.tone}`}><q.icon className="w-5 h-5" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-ink-900 truncate">{q.label}</p>
              <p className="text-xs text-ink-500 truncate">{q.desc}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-ink-300" />
          </Link>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Events by Location */}
        <div className="card">
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-dental-600" />
            Events by Location
          </h3>
          {eventsByLocation && eventsByLocation.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <ResponsiveContainer width="100%" height={300} minWidth={300}>
                <BarChart data={eventsByLocation}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="location" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: '#f1f5f9' }} />
                  <Bar
                    dataKey="count"
                    fill="#0d9488"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(d) => d?.location && navigate(`/locations/${encodeURIComponent(d.location)}`)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8 text-sm">No location data available</p>
          )}
        </div>

        {/* Events Timeline */}
        <div className="card">
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-dental-600" />
            Events Discovered (Last 30 Days)
          </h3>
          {eventsTimeline && eventsTimeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={eventsTimeline}>
                <defs>
                  <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d9488" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#0d9488" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  tickFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip 
                  labelFormatter={(date) => {
                    const d = new Date(date);
                    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                  }}
                  formatter={(value) => [`${value} events`, 'Discovered']}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#0d9488" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorEvents)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-8 text-sm">No timeline data available</p>
          )}
        </div>
      </div>

      {/* Upcoming Events & Top Clinics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Upcoming Events */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-dental-600" />
              Upcoming Events
            </h3>
            <Link to="/calendar" className="text-dental-600 hover:text-dental-700 text-xs sm:text-sm font-medium">
              View Calendar →
            </Link>
          </div>
          {upcomingEvents && upcomingEvents.length > 0 ? (
            <div className="space-y-3">
              {upcomingEvents.slice(0, 5).map((event) => (
                <div key={event.id} className="flex flex-col sm:flex-row items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="bg-dental-100 text-dental-700 rounded px-2 py-1 text-xs font-medium sm:min-w-[70px] text-center">
                    {event.startDate || event.event_start_date}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{event.eventTitle || 'Untitled Event'}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {event.clinicName && (
                        <Link to={`/clinics/${encodeURIComponent(event.clinicName)}`} className="hover:text-dental-600 hover:underline">
                          {event.clinicName}
                        </Link>
                      )}
                      {event.clinicName && event.location && ' • '}
                      {event.location && (
                        <Link to={`/locations/${encodeURIComponent(event.location)}`} className="hover:text-dental-600 hover:underline">
                          {event.location}
                        </Link>
                      )}
                    </p>
                    {event.offer && (
                      <span className="badge badge-success mt-1 text-xs">{event.offer}</span>
                    )}
                  </div>
                  {event.adUrl && (
                    <a
                      href={event.adUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 shrink-0"
                    >
                      View Ad
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8 text-sm">No upcoming events</p>
          )}
        </div>

        {/* Top Clinics */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-dental-600" />
              Top Clinics by Events
            </h3>
            <Link to="/clinics" className="text-dental-600 hover:text-dental-700 text-xs sm:text-sm font-medium">
              All clinics →
            </Link>
          </div>
          {topClinics && topClinics.length > 0 ? (
            <div className="space-y-3">
              {topClinics.map((clinic, index) => (
                <button
                  key={clinic.clinicName}
                  onClick={() => navigate(`/clinics/${encodeURIComponent(clinic.clinicName)}`)}
                  className="flex items-center gap-3 w-full text-left hover:bg-gray-50 rounded-lg px-2 py-1 -mx-2 transition-colors"
                >
                  <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-gray-100 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-50 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{clinic.clinicName}</p>
                  </div>
                  <div className="text-sm font-semibold text-dental-600 shrink-0">
                    {clinic.eventCount} events
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8 text-sm">No clinic data available</p>
          )}
        </div>
      </div>

      {/* Treatment Types */}
      {treatmentTypes && treatmentTypes.length > 0 && (
        <div className="card">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Popular Treatment Types</h3>
          <div className="flex flex-wrap gap-2">
            {treatmentTypes.map(({ treatment, count }) => (
              <span key={treatment} className="badge badge-info text-xs sm:text-sm">
                {treatment} ({count})
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
